import axios from 'axios'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css' // Import the CSS for styling
import router from '@/router'

// Track whether we've successfully authenticated in this session.
// When false, 401s are handled by the router auth middleware (which
// redirects to login with the intended URL preserved). When true,
// a 401 means the session expired mid-use and we need to force logout.
let hasEstablishedSession = false

// Callback to clear auth store state on session expiry.
// Set by authStore to avoid circular dependency (axiosApi ↔ authStore).
let onSessionExpired: (() => void) | null = null

export function registerSessionExpiredCallback(callback: () => void) {
  onSessionExpired = callback
}

export function markSessionEstablished() {
  hasEstablishedSession = true
}

export function clearSessionFlag() {
  hasEstablishedSession = false
}

export function isSessionEstablished() {
  return hasEstablishedSession
}

export const noAuthApi = axios.create({
  baseURL: import.meta.env.VITE_APP_BACKEND_HOST,
  withCredentials: false,
  withXSRFToken: false,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})

const api = axios.create({
  baseURL: import.meta.env.VITE_APP_BACKEND_HOST,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})

noAuthApi.interceptors.request.use((config) => {
  NProgress.start()
  return config
})

noAuthApi.interceptors.response.use(
  (response) => {
    NProgress.done()
    return response
  },
  (error) => {
    NProgress.done()
    return Promise.reject(error)
  }
)

// Request interceptor
api.interceptors.request.use((config) => {
  NProgress.start()
  return config
})

// Response interceptor
api.interceptors.response.use(
  (response) => {
    NProgress.done()
    return response
  },
  (error) => {
    NProgress.done()

    // Check for 503 maintenance mode
    if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
      const systemStore = useSystemStore()
      systemStore.setMaintenance(
        true,
        error.response.data.message,
        error.response.data.estimatedEnd,
      )
    }

    // Check for 401 Unauthorized error
    if (error.response?.status === 401) {
      const publicRoutes = ['login', 'register', 'forgot-password', 'reset-password', 'agency-signup']
      const currentRouteName = router.currentRoute?.value?.name as string | undefined

      if (currentRouteName && !publicRoutes.includes(currentRouteName)) {
        if (hasEstablishedSession) {
          // Session expired mid-use. Clear local state and redirect to login.
          // IMPORTANT: Do NOT call api.post('/logout') here — the session is
          // already invalid server-side. Hitting /logout would destroy the
          // shared session cookie (.rightmessage.com), which kills sessions
          // on other subdomains too (e.g., Filament admin panel).
          hasEstablishedSession = false
          onSessionExpired?.()
          router.push({ name: 'login' })
        }
        // Otherwise: initial page load or admin team switch where session
        // isn't established yet. Let the router auth middleware handle it —
        // it redirects to login with the intended URL preserved.
      }
    }

    return Promise.reject(error)
  }
)

export default api
